import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {State} from '../../../../reducers'
import {getTiposContingencia} from '../../../../actions/catalogos.actions'
import {ModalBase} from '../../../../components/Modals/ModalBase'
import {getTrazabilidadContingencia} from '../../../../actions/contingencia/contingencia.actions'
import DownloadJson from '../../../../media/felsv/DownloadJson.svg'
import '../../../OtrosDocumentos/styles/otros-documentos.css'
import './modal-bitacora-contingencia.css'

/**
 * Los tres pasos del proceso de contingencia, dichos como los vive quien factura.
 * Hacienda los llama momentos 1, 2 y 3 en su Manual Tecnico; ese vocabulario se queda
 * adentro y aqui se nombra lo que de verdad le pasa al documento.
 */
const PASOS: {[llave: string]: string} = {
  '1': 'Documento emitido sin conexión',
  '2': 'Aviso enviado a Hacienda',
  '3': 'Documento certificado por Hacienda',
}

const PASOS_DETALLE: {[llave: string]: string} = {
  '1': 'El comprobante ya se entregó al cliente. Falta avisarle a Hacienda.',
  '2': 'Hacienda ya sabe de la contingencia. Falta enviarle el documento para que lo certifique.',
  '3': 'Hacienda recibió el documento y le otorgó su sello. El proceso terminó.',
}

const ESTADOS: {[llave: string]: {texto: string; clase: string}} = {
  GENERADO: {texto: 'Pendiente de avisar a Hacienda', clase: 'es-contingencia'},
  INFORMADO: {texto: 'Pendiente de certificar', clase: 'es-contingencia'},
  TRANSMITIDO: {texto: 'Certificado por Hacienda', clase: 'es-certificado'},
  RECHAZADO: {texto: 'Aviso rechazado por Hacienda', clase: 'es-error'},
}

const EVENTOS: {[llave: string]: string} = {
  INTENTO_1: 'Primer intento de envío',
  INTENTO_2: 'Segundo intento de envío',
  INTENTO_3: 'Tercer intento de envío',
  CONSULTA_1: '¿Hacienda ya lo recibió?',
  CONSULTA_2: '¿Hacienda ya lo recibió?',
  CONSULTA_DTE: '¿Hacienda ya lo recibió?',
  CONTINGENCIA_APLICADA: 'Documento emitido en contingencia',
  EVENTO_FIRMA: 'Firma del aviso de contingencia',
  EVENTO_ENVIADO: 'Aviso de contingencia enviado',
  EVENTO_RECHAZADO: 'Aviso de contingencia rechazado',
  LOTE_ENVIADO: 'Documento enviado a Hacienda',
  LOTE_RECHAZADO: 'Envío rechazado por Hacienda',
  LOTE_CONSULTADO: 'Consulta del envío',
  DTE_SELLADO: 'Hacienda certificó el documento',
  DTE_RECHAZADO: 'Hacienda rechazó el documento',
}

const RESULTADOS: {[llave: string]: {texto: string; clase: string}} = {
  OK: {texto: 'Correcto', clase: 'es-completado'},
  RECIBIDO: {texto: 'Recibido', clase: 'es-completado'},
  PROCESADO: {texto: 'Procesado', clase: 'es-completado'},
  PROCESANDO: {texto: 'En proceso', clase: 'es-proceso'},
  SIN_CONEXION: {texto: 'Sin respuesta', clase: 'es-error'},
  SIN_RESPUESTA: {texto: 'Sin respuesta', clase: 'es-error'},
  TIMEOUT: {texto: 'Tiempo agotado', clase: 'es-error'},
  RECHAZADO: {texto: 'Rechazado', clase: 'es-error'},
  ERROR: {texto: 'Error', clase: 'es-error'},
}

const ModalBitacoraContingencia = (props: any) => {
  const {codigoGeneracion, numeroControl, onClose} = props

  const dispatch = useDispatch()
  const {tiposContingencia} = useSelector((state: State) => state.catalogos)
  const [registro, setRegistro] = useState<any>(null)
  const [consultado, setConsultado] = useState(false)

  useEffect(() => {
    dispatch(getTiposContingencia() as any)
  }, [dispatch])

  const nombreTipoContingencia = (codigo: any): string => {
    const buscado = `${codigo ?? ''}`.trim()
    if (!buscado) return ''
    const encontrado = (tiposContingencia ?? []).find(
      (tipo: any) => `${tipo?.codigo ?? ''}`.trim().replace(/^0+/, '') === buscado.replace(/^0+/, '')
    )
    return encontrado?.valor ?? ''
  }

  useEffect(() => {
    const consultar = async () => {
      const datos: any = await dispatch(getTrazabilidadContingencia(codigoGeneracion))
      setRegistro(datos)
      setConsultado(true)
    }

    if (codigoGeneracion) consultar()
  }, [codigoGeneracion])

  const dato = (valor: any, alterno = '--') =>
    valor === null || valor === undefined || valor === '' ? alterno : valor

  const pastilla = (mapa: any, llave: string, alterno = '') => {
    const item = mapa[llave]
    if (!item) return <span>{dato(llave || alterno)}</span>
    return <div className={`bitacora-cont-estado ${item.clase}`}>{item.texto}</div>
  }

  const leerJson = (texto: string) => {
    try {
      return JSON.parse(texto)
    } catch (error) {
      return texto
    }
  }

  /**
   * Descarga lo que se le mandó a Hacienda y lo que Hacienda contestó en ese paso. Es la
   * evidencia que se le puede presentar a Hacienda si pregunta por el documento.
   */
  const descargarIntercambio = (entrada: any) => {
    const contenido = {
      documento: numeroControl,
      codigoGeneracion,
      paso: `${dato(entrada.momento)} de 3 - ${dato(PASOS[entrada.momento])}`,
      accion: dato(EVENTOS[entrada.evento] ?? entrada.evento),
      fechaHora: dato(entrada.fechaHora),
      resultado: dato(RESULTADOS[entrada.resultado]?.texto ?? entrada.resultado),
      duracionMs: entrada.duracionMs ? Number(entrada.duracionMs) : null,
      enviadoAHacienda: entrada.requestJson ? leerJson(entrada.requestJson) : null,
      respuestaDeHacienda: entrada.responseJson ? leerJson(entrada.responseJson) : null,
      nota: entrada.responseJson
        ? undefined
        : 'Hacienda no devolvió respuesta en este intento; por eso el documento siguió su camino a contingencia.',
    }

    const archivo = new Blob([JSON.stringify(contenido, null, 2)], {type: 'application/json'})
    const enlace = document.createElement('a')

    enlace.href = URL.createObjectURL(archivo)
    enlace.download = `${numeroControl || codigoGeneracion}_${entrada.evento}.json`
    document.body.appendChild(enlace)
    enlace.click()
    document.body.removeChild(enlace)
    URL.revokeObjectURL(enlace.href)
  }

  const hayIntercambio = (entrada: any) => !!(entrada?.requestJson || entrada?.responseJson)

  return (
    <ModalBase Styles={{maxWidth: '980px'}}>
      <div className='modal-card bitacora-cont-card'>
        <div className='modal-content'>
          <div className='w-layout-hflex'>
            <h3 className='card-title l'>Seguimiento de la contingencia</h3>
          </div>
          <p className='bitacora-cont-subtitulo'>{dato(numeroControl)}</p>
          <div className='form-separator'></div>

          <div className='bitacora-cont-cuerpo'>

          {consultado && !registro && (
            <p className='bitacora-cont-vacia'>
              Este documento no tiene registro de contingencia.
            </p>
          )}

          {registro && (
            <>
              <p className='bitacora-cont-intro'>
                Este documento se emitió cuando Hacienda no estaba disponible. Aquí puede ver
                por qué ocurrió, en qué paso va y qué se intercambió con Hacienda.
              </p>

              <div className='bitacora-cont-secciones'>
                <div className='dte-subsection'>
                  <div className='dte-subsection-title'>Situación del documento</div>
                  <table className='dte-table'>
                    <tbody>
                      <tr>
                        <td>Estado:</td>
                        <td>{pastilla(ESTADOS, registro.estadoContingencia)}</td>
                      </tr>
                      <tr>
                        <td>Paso actual:</td>
                        <td>
                          {dato(PASOS[registro.momento])}
                          <div className='bitacora-cont-etapa'>
                            Paso {dato(registro.momento)} de 3
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td>Qué sigue:</td>
                        <td>{dato(PASOS_DETALLE[registro.momento])}</td>
                      </tr>
                      <tr>
                        <td>Por qué ocurrió:</td>
                        <td>{dato(nombreTipoContingencia(registro.tipoContingencia))}</td>
                      </tr>
                      <tr>
                        <td>Intentos de envío:</td>
                        <td>{dato(registro.intentos)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className='dte-subsection'>
                  <div className='dte-subsection-title'>Aviso a Hacienda y plazos</div>
                  <table className='dte-table'>
                    <tbody>
                      <tr>
                        <td>Sello del aviso:</td>
                        <td>{dato(registro.eventoSelloRecibido)}</td>
                      </tr>
                      <tr>
                        <td>Fecha límite para avisar:</td>
                        <td>{dato(registro.fechaLimiteEvento)}</td>
                      </tr>
                      <tr>
                        <td>Fecha límite para certificar:</td>
                        <td>{dato(registro.fechaLimiteTransmision)}</td>
                      </tr>
                      <tr>
                        <td>Número de envío:</td>
                        <td>{dato(registro.codigoLote)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {registro.motivoContingencia && (
                <p className='bitacora-cont-motivo'>
                  <strong>Detalle del motivo:&nbsp;</strong>
                  {registro.motivoContingencia}
                </p>
              )}

              <div className='form-separator'></div>

              <div className='tab-plane'>
                <div className='table-facturacion-resp bitacora-cont-tabla'>
                  <table className='table-main-responsive responsive-table-main td-left'>
                    <thead>
                      <tr>
                        <td>Fecha y hora</td>
                        <td>Qué se hizo</td>
                        <td>Resultado</td>
                        <td>Respuesta de Hacienda</td>
                        <td>JSON</td>
                      </tr>
                    </thead>
                    <tbody>
                      {(registro.bitacora ?? []).map((entrada: any, indice: number) => (
                        <tr key={indice}>
                          <td data-label='Fecha y hora'>{dato(entrada.fechaHora)}</td>
                          <td data-label='Qué se hizo'>
                            {dato(EVENTOS[entrada.evento] ?? entrada.evento)}
                            <div className='bitacora-cont-etapa'>
                              Paso {dato(entrada.momento)}
                            </div>
                          </td>
                          <td data-label='Resultado'>
                            {pastilla(RESULTADOS, entrada.resultado)}
                            {entrada.duracionMs ? (
                              <div className='bitacora-cont-duracion'>{entrada.duracionMs} ms</div>
                            ) : null}
                          </td>
                          <td data-label='Respuesta de Hacienda'>
                            {dato(entrada.descripcionMsg)}
                          </td>
                          <td data-label='JSON'>
                            {hayIntercambio(entrada) ? (
                              <button
                                type='button'
                                className='bitacora-cont-descarga'
                                title='Descargar lo que se envió a Hacienda y su respuesta'
                                onClick={() => descargarIntercambio(entrada)}
                              >
                                <img src={DownloadJson} alt='Descargar JSON' />
                              </button>
                            ) : (
                              <span className='bitacora-cont-etapa'>--</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {(registro.bitacora ?? []).length === 0 && (
                  <p className='bitacora-cont-vacia'>
                    No hay entradas registradas para este documento.
                  </p>
                )}
              </div>
            </>
          )}

          </div>

          <div className='form-separator'></div>
          <div className='w-layout-hflex button-actions'>
            <button className='m button transparent w-inline-block' onClick={onClose}>
              <div>Cerrar</div>
            </button>
          </div>
        </div>
      </div>
    </ModalBase>
  )
}

export default ModalBitacoraContingencia
